/**
 * CompatibilityCard Component - Enhanced compatibility settings card with modern UI
 */
import { __ } from '@wordpress/i18n';
import {
    Card,
    Switch,
    Space,
    Typography,
    Row,
    Col,
    InputNumber,
    Form,
    Tooltip,
    Divider,
    Tag,
    Badge,
    Avatar,
    Progress,
    Alert
} from 'antd';
import {
    ApiOutlined,
    MobileOutlined,
    TabletOutlined,
    DesktopOutlined,
    InfoCircleOutlined,
    SettingOutlined,
    ShieldCheckOutlined,
    CheckOutlined,
    BugOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const CompatibilityCard = ({ settings, onUpdate }) => {
    const getCompatibilityScore = () => {
        const activeFeatures = [
            settings.theme_compatibility,
            settings.plugin_compatibility
        ].filter(Boolean).length;
        return Math.round((activeFeatures / 2) * 100);
    };

    const score = getCompatibilityScore();
    const cardStyle = {
        marginBottom: 24,
        borderRadius: 12,
        overflow: 'hidden',
        border: score > 0 ? '2px solid #13c2c2' : '1px solid #f0f0f0',
        boxShadow: score > 0
            ? '0 4px 20px rgba(19, 194, 194, 0.15)'
            : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    return (
        <Card
            style={cardStyle}
            bodyStyle={{ padding: 0 }}
            bordered={false}
        >
            {/* Header Section with Gradient */}
            <div style={{
                background: score > 0
                    ? 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)'
                    : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                padding: '20px 24px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Background Pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100px',
                    height: '100px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(30px, -30px)'
                }} />

                <Row align="middle" justify="space-between">
                    <Col>
                        <Space size="large" align="center">
                            <Avatar
                                size={48}
                                style={{
                                    backgroundColor: score > 0 ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                                    color: score > 0 ? '#fff' : '#13c2c2',
                                    fontSize: '20px',
                                    border: score > 0 ? '2px solid rgba(255,255,255,0.3)' : 'none'
                                }}
                                icon={<SafetyCertificateOutlined />}
                            />
                            <div>
                                <Space size="small" align="center">
                                    <Text style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: score > 0 ? '#fff' : '#262626',
                                        textShadow: score > 0 ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                                    }}>
                                        {__('Compatibility Settings', 'marqueex')}
                                    </Text>
                                    <Tag color="cyan" style={{ margin: 0 }}>
                                        {__('Safety', 'marqueex')}
                                    </Tag>
                                </Space>
                                <div style={{ marginTop: 4 }}>
                                    <Badge
                                        status={score > 50 ? 'success' : 'default'}
                                        text={
                                            <Text style={{
                                                color: score > 0 ? 'rgba(255,255,255,0.9)' : '#666',
                                                fontSize: '12px'
                                            }}>
                                                {score > 50 ? __('Protected', 'marqueex') : __('Basic', 'marqueex')}
                                            </Text>
                                        }
                                    />
                                </div>
                            </div>
                        </Space>
                    </Col>
                    <Col>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                size={40}
                                percent={score}
                                strokeColor={score > 0 ? '#fff' : '#13c2c2'}
                                trailColor="rgba(255,255,255,0.3)"
                                strokeWidth={8}
                                format={() => (
                                    <Text style={{
                                        color: score > 0 ? '#fff' : '#13c2c2',
                                        fontSize: '10px',
                                        fontWeight: 600
                                    }}>
                                        {score}%
                                    </Text>
                                )}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Content Section */}
            <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: 20 }}>
                    <Paragraph style={{
                        marginBottom: 16,
                        color: "#666",
                        fontSize: '14px',
                        lineHeight: '1.6'
                    }}>
                        {__('Configure compatibility modes and responsive breakpoints to ensure MarqueeX works seamlessly with your theme and other plugins.', 'marqueex')}
                    </Paragraph>

                    {/* Feature Tags */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong style={{ fontSize: '12px', color: '#999', marginBottom: 8, display: 'block' }}>
                            {__('Protection Features:', 'marqueex')}
                        </Text>
                        <Space size={[4, 8]} wrap>
                            {['Theme Safety', 'Plugin Isolation', 'Responsive Design', 'Cross-Browser'].map((feature, index) => (
                                <Tag
                                    key={index}
                                    style={{
                                        margin: 0,
                                        borderRadius: 12,
                                        fontSize: '11px',
                                        border: '1px solid #13c2c220',
                                        backgroundColor: '#13c2c210',
                                        color: '#13c2c2'
                                    }}
                                >
                                    {feature}
                                </Tag>
                            ))}
                        </Space>
                    </div>
                </div>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Compatibility Modes */}
                <div>
                    <Text strong style={{ fontSize: '14px', marginBottom: 12, display: 'block' }}>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        {__('Compatibility Modes', 'marqueex')}
                    </Text>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div style={{
                                padding: '20px',
                                backgroundColor: '#f6ffed',
                                borderRadius: 12,
                                border: '2px solid #b7eb8f',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#52c41a',
                                    borderRadius: '50%',
                                    opacity: 0.1
                                }} />
                                <Row align="middle" justify="space-between">
                                    <Col flex="1">
                                        <Space size="middle" align="center">
                                            <Avatar
                                                size={40}
                                                style={{
                                                    backgroundColor: '#52c41a20',
                                                    color: '#52c41a',
                                                    border: '2px solid #52c41a30'
                                                }}
                                                icon={<CheckOutlined />}
                                            />
                                            <div>
                                                <Text strong style={{ fontSize: '15px', display: 'block' }}>
                                                    {__('Theme Compatibility Mode', 'marqueex')}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {__('Activates CSS isolation and JavaScript compatibility fixes', 'marqueex')}
                                                </Text>
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag color="green" size="small">
                                                        {__('Recommended for theme conflicts', 'marqueex')}
                                                    </Tag>
                                                </div>
                                            </div>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Switch
                                            checked={settings.theme_compatibility}
                                            onChange={(checked) => onUpdate('theme_compatibility', checked)}
                                            style={{ backgroundColor: settings.theme_compatibility ? '#52c41a' : undefined }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        <Col span={24}>
                            <div style={{
                                padding: '20px',
                                backgroundColor: '#fff7e6',
                                borderRadius: 12,
                                border: '2px solid #ffd591',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -10,
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#fa8c16',
                                    borderRadius: '50%',
                                    opacity: 0.1
                                }} />
                                <Row align="middle" justify="space-between">
                                    <Col flex="1">
                                        <Space size="middle" align="center">
                                            <Avatar
                                                size={40}
                                                style={{
                                                    backgroundColor: '#fa8c1620',
                                                    color: '#fa8c16',
                                                    border: '2px solid #fa8c1630'
                                                }}
                                                icon={<BugOutlined />}
                                            />
                                            <div>
                                                <Text strong style={{ fontSize: '15px', display: 'block' }}>
                                                    {__('Plugin Compatibility Mode', 'marqueex')}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {__('Prevents conflicts with namespaced CSS and isolated execution', 'marqueex')}
                                                </Text>
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag color="orange" size="small">
                                                        {__('Recommended for plugin conflicts', 'marqueex')}
                                                    </Tag>
                                                </div>
                                            </div>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Switch
                                            checked={settings.plugin_compatibility}
                                            onChange={(checked) => onUpdate('plugin_compatibility', checked)}
                                            style={{ backgroundColor: settings.plugin_compatibility ? '#fa8c16' : undefined }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </div>

                <Divider />

                {/* Responsive Breakpoints */}
                <div>
                    <Text strong style={{ fontSize: '14px', marginBottom: 12, display: 'block' }}>
                        <MobileOutlined style={{ marginRight: 8 }} />
                        {__('Responsive Breakpoints', 'marqueex')}
                    </Text>

                    <Form layout="vertical">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={8}>
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
                                    borderRadius: 12,
                                    border: '2px solid #91d5ff',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: -5,
                                        right: -5,
                                        width: 30,
                                        height: 30,
                                        backgroundColor: '#1890ff',
                                        borderRadius: '50%',
                                        opacity: 0.1
                                    }} />
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Avatar
                                            size={48}
                                            style={{
                                                backgroundColor: '#1890ff20',
                                                color: '#1890ff',
                                                border: '2px solid #1890ff30'
                                            }}
                                            icon={<MobileOutlined />}
                                        />
                                        <Text strong style={{ fontSize: '16px' }}>{__('Mobile', 'marqueex')}</Text>
                                        <Form.Item style={{ margin: 0 }}>
                                            <InputNumber
                                                value={settings.breakpoints?.mobile || 768}
                                                onChange={(value) => onUpdate('breakpoints', { ...settings.breakpoints, mobile: value })}
                                                min={320}
                                                max={1024}
                                                formatter={value => `${value}px`}
                                                parser={value => value.replace('px', '')}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </Form.Item>
                                        <Tag color="blue" style={{ fontSize: '11px', borderRadius: 12 }}>
                                            {__('Default: 768px', 'marqueex')}
                                        </Tag>
                                    </Space>
                                </div>
                            </Col>

                            <Col xs={24} sm={8}>
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9e8 100%)',
                                    borderRadius: 12,
                                    border: '2px solid #b7eb8f',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: -5,
                                        right: -5,
                                        width: 30,
                                        height: 30,
                                        backgroundColor: '#52c41a',
                                        borderRadius: '50%',
                                        opacity: 0.1
                                    }} />
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Avatar
                                            size={48}
                                            style={{
                                                backgroundColor: '#52c41a20',
                                                color: '#52c41a',
                                                border: '2px solid #52c41a30'
                                            }}
                                            icon={<TabletOutlined />}
                                        />
                                        <Text strong style={{ fontSize: '16px' }}>{__('Tablet', 'marqueex')}</Text>
                                        <Form.Item style={{ margin: 0 }}>
                                            <InputNumber
                                                value={settings.breakpoints?.tablet || 1024}
                                                onChange={(value) => onUpdate('breakpoints', { ...settings.breakpoints, tablet: value })}
                                                min={768}
                                                max={1440}
                                                formatter={value => `${value}px`}
                                                parser={value => value.replace('px', '')}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </Form.Item>
                                        <Tag color="green" style={{ fontSize: '11px', borderRadius: 12 }}>
                                            {__('Default: 1024px', 'marqueex')}
                                        </Tag>
                                    </Space>
                                </div>
                            </Col>

                            <Col xs={24} sm={8}>
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #fff2e8 0%, #ffeee0 100%)',
                                    borderRadius: 12,
                                    border: '2px solid #ffbb96',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: -5,
                                        right: -5,
                                        width: 30,
                                        height: 30,
                                        backgroundColor: '#fa8c16',
                                        borderRadius: '50%',
                                        opacity: 0.1
                                    }} />
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Avatar
                                            size={48}
                                            style={{
                                                backgroundColor: '#fa8c1620',
                                                color: '#fa8c16',
                                                border: '2px solid #fa8c1630'
                                            }}
                                            icon={<DesktopOutlined />}
                                        />
                                        <Text strong style={{ fontSize: '16px' }}>{__('Desktop', 'marqueex')}</Text>
                                        <Form.Item style={{ margin: 0 }}>
                                            <InputNumber
                                                value={settings.breakpoints?.desktop || 1200}
                                                onChange={(value) => onUpdate('breakpoints', { ...settings.breakpoints, desktop: value })}
                                                min={1024}
                                                max={2560}
                                                formatter={value => `${value}px`}
                                                parser={value => value.replace('px', '')}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </Form.Item>
                                        <Tag color="orange" style={{ fontSize: '11px', borderRadius: 12 }}>
                                            {__('Default: 1200px', 'marqueex')}
                                        </Tag>
                                    </Space>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Space>
            </div>
        </Card>
    );
};

export default CompatibilityCard;
